<section class="sd">
  <button type="button" class="back" (click)="back.emit()">
    <i class="fa fa-arrow-left" aria-hidden="true"></i> Back to calendar
  </button>

  <header class="sd-head">
    <div>
      <h2 class="sd-title">
        <span class="brk">{{ form.schedule_id }}</span>
        <span class="pill" [ngClass]="statusClass">{{ form.status }}</span>
      </h2>
      <p class="sd-sub">{{ form.window_text }}</p>
      <p class="sd-sub" *ngIf="movedFrom">Moved from {{ movedFrom }}</p>
    </div>

    <div class="sd-actions" *ngIf="editable">
      <button type="button" class="btn-outline" [disabled]="busy != 'idle'" (click)="editRequest.emit()">
        <i class="fa fa-pencil" aria-hidden="true"></i> Edit schedule
      </button>
      <button type="button" class="btn-danger-outline" [disabled]="busy != 'idle'" (click)="cancelRequest.emit()">
        <i class="fa fa-ban" aria-hidden="true"></i> Cancel schedule
      </button>
      <button type="button" class="btn-outline" [disabled]="busy != 'idle'" (click)="checkRequest.emit()">
        <i class="fa fa-check-square-o" aria-hidden="true"></i> Check
      </button>
    </div>
    <span class="readonly" *ngIf="!editable">
      <i class="fa fa-lock" aria-hidden="true"></i> {{ form.status }} schedules can&rsquo;t be changed
    </span>
  </header>

  <!-- Failure summary -->
  <div class="alert" role="alert" *ngIf="failureReason || failedInstances.length">
    <strong>{{ form.status == 'Failed' ? 'This run failed' : 'This run had issues' }}</strong>
    <p *ngIf="failureReason">{{ failureReason }}</p>
    <ul *ngIf="failedInstances.length">
      <li *ngFor="let f of failedInstances; trackBy: trackByIndex">
        <span class="mono">{{ f.instance_id }}</span>
        <span *ngIf="f.name"> ({{ f.name }})</span> &mdash; {{ f.patch_status }}<span *ngIf="f.details">: {{ f.details }}</span>
      </li>
    </ul>
  </div>

  <!-- Facts -->
  <dl class="facts">
    <div><dt>Customer</dt><dd>{{ form.customer }}</dd></div>
    <div><dt>Servers</dt><dd>{{ serverList.length }}</dd></div>
    <div><dt>Reboot</dt><dd>{{ rebootText() }}</dd></div>
    <div><dt>Timezone</dt><dd>{{ form.timezone }}</dd></div>
    <div><dt>Version</dt><dd>{{ record?.version }}</dd></div>
    <div><dt>Last updated</dt><dd>{{ utc(record?.updated_at) }}</dd></div>
  </dl>

  <!-- Tabs -->
  <div class="tabs" role="tablist" aria-label="Schedule details">
    <button *ngFor="let t of tabs" type="button" role="tab" class="tab"
      [class.active]="tab == t.id" [attr.aria-selected]="tab == t.id" (click)="setTab(t.id)">
      {{ t.label }}<span class="count" *ngIf="t.count != null">{{ t.count }}</span>
    </button>
  </div>

  <!-- Servers -->
  <div *ngIf="tab == 'servers'" role="tabpanel">
    <p class="muted" *ngIf="removedLabels.length">Removed from this window: {{ removedLabels.join(', ') }}</p>
    <div class="table-wrap">
      <table class="grid">
        <thead><tr>
          <th scope="col">Server</th><th scope="col">Account</th><th scope="col">OS</th>
          <th scope="col">Owner</th><th scope="col">Application</th><th scope="col">Env</th>
        </tr></thead>
        <tbody>
          <tr *ngFor="let s of serverList; trackBy: trackByIndex">
            <td><div>{{ s.name || '\u2014' }}</div><div class="muted mono brk">{{ s.instance_id }}</div></td>
            <td><div class="mono">{{ s.account_id }}</div><div class="muted">{{ s.region }}</div></td>
            <td>{{ s.os }}</td>
            <td>{{ s.owner }}</td>
            <td>{{ s.application }}</td>
            <td>{{ s.customer_env }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <!-- Run results -->
  <div *ngIf="tab == 'run' && run as r" role="tabpanel">
    <dl class="facts compact">
      <div><dt>Started</dt><dd>{{ utc(r.started_at) }}</dd></div>
      <div><dt>Finished</dt><dd>{{ utc(r.finished_at) }}</dd></div>
      <div><dt>Succeeded</dt><dd class="ok">{{ summary.succeeded }} of {{ summary.instances }}</dd></div>
      <div><dt>Not succeeded</dt><dd [class.err]="summary.not_succeeded > 0">{{ summary.not_succeeded }}</dd></div>
    </dl>
    <p class="err-text" *ngFor="let e of runGroupErrors">{{ e }}</p>
    <div class="table-wrap">
      <table class="grid">
        <thead><tr>
          <th scope="col">Server</th><th scope="col">Pre-check</th><th scope="col">Post-check</th>
          <th scope="col" class="num">Patches</th><th scope="col">Status</th><th scope="col">Details</th>
        </tr></thead>
        <tbody>
          <tr *ngFor="let i of runInstances; trackBy: trackByIndex">
            <td><div>{{ i.name || '\u2014' }}</div><div class="muted mono brk">{{ i.instance_id }}</div></td>
            <td>{{ i.pre_check }}</td>
            <td>{{ i.post_check }}</td>
            <td class="num">{{ i.patches_installed }}</td>
            <td><span class="pill" [ngClass]="patchClass(i.patch_status)">{{ i.patch_status }}</span></td>
            <td>{{ i.details }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <ul class="reports" *ngIf="runReports.length">
      <li *ngFor="let f of runReports"><i class="fa fa-file-pdf-o" aria-hidden="true"></i> {{ fileName(f) }}</li>
    </ul>
  </div>

  <!-- Notices -->
  <div *ngIf="tab == 'notices'" role="tabpanel" class="table-wrap">
    <table class="grid">
      <thead><tr>
        <th scope="col">Notice</th><th scope="col">Scheduled for</th><th scope="col">Status</th><th scope="col">Sent</th>
      </tr></thead>
      <tbody>
        <tr *ngFor="let n of noticeList; trackBy: trackByIndex">
          <td>{{ n.notice }}</td>
          <td>{{ at(n.send_at) }}</td>
          <td><span class="pill" [ngClass]="noticeClass(n)">{{ n.status }}</span></td>
          <td>{{ n.sent_at ? utc(n.sent_at) : '\u2014' }}</td>
        </tr>
      </tbody>
    </table>
  </div>

  <!-- Teams -->
  <div *ngIf="tab == 'teams'" role="tabpanel" class="table-wrap">
    <table class="grid">
      <thead><tr>
        <th scope="col">Owner</th><th scope="col">Customer emails</th><th scope="col">Internal emails</th>
        <th scope="col" class="num">Servers</th>
      </tr></thead>
      <tbody>
        <tr *ngFor="let t of teamList; trackBy: trackByIndex">
          <td>{{ t.owner }}</td>
          <td class="brk">{{ join(t.customer_emails) }}</td>
          <td class="brk">{{ join(t.internal_emails) }}</td>
          <td class="num">{{ count(t.instance_ids) }}</td>
        </tr>
      </tbody>
    </table>
  </div>

  <!-- History -->
  <ol class="timeline" *ngIf="tab == 'history'" role="tabpanel">
    <li *ngFor="let h of historyList; trackBy: trackHistory">
      <span class="dot" aria-hidden="true"></span>
      <div>
        <div>{{ h.event }}</div>
        <div class="muted">{{ utc(h.at) }} &middot; {{ h.by }}</div>
      </div>
    </li>
  </ol>
</section>